import React, { useMemo } from "react";
import { useTranslate } from "@refinedev/core";
import { useLocation, useNavigate } from "react-router-dom";
import { Tabs, type TabItem } from "@libertify/ui";
import { FeatureFlags } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeature";
import { useAnalyticsBackState } from "../../projects/hooks/useAnalyticsBack";

const ANALYTICS_SEGMENT = "/analytics";

const TAB_ROUTES: Record<string, string> = {
  overview: "",
  video: "/video-explainer",
  document: "/document-highlighter",
  chat: "/ai-chatbot",
  addons: "/addons",
  activity: "/activity",
};

const TAB_KEYS = Object.keys(TAB_ROUTES);

export const AnalyticsTabs: React.FC = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const location = useLocation();
  const backState = useAnalyticsBackState();
  const isAddonsEnabled = useFeatureFlag(FeatureFlags.AddonsAnalytics);

  const basePath = useMemo(() => {
    const idx = location.pathname.indexOf(ANALYTICS_SEGMENT);
    if (idx === -1) {
      return location.pathname;
    }
    return location.pathname.slice(0, idx + ANALYTICS_SEGMENT.length);
  }, [location.pathname]);

  const activeKey = useMemo(() => {
    const suffix = location.pathname.slice(basePath.length);
    const match = TAB_KEYS.find((key) => TAB_ROUTES[key] === suffix);
    return match ?? "overview";
  }, [location.pathname, basePath]);

  const items = useMemo<TabItem[]>(
    () =>
      TAB_KEYS.filter((key) => key !== "addons" || isAddonsEnabled).map(
        (key) => ({
          key,
          label: t(`media.projects.analytics.tabs.${key}`),
        })
      ),
    [t, isAddonsEnabled]
  );

  const handleChange = (key: string) => {
    if (key === activeKey) {
      return;
    }
    navigate(`${basePath}${TAB_ROUTES[key]}${location.search}`, {
      replace: true,
      state: backState,
    });
  };

  return (
    <Tabs
      variant="line"
      items={items}
      activeKey={activeKey}
      onChange={handleChange}
      tabBarStyle={{ marginBottom: 0 }}
    />
  );
};
